import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, unset } from 'lodash';

import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import {
  AssignmentHistoryDetailsModel,
  AssignmentHistoryResponse,
  AssignmentMultiRateEditModel,
} from 'api/adminApiClient';
import {
  getOvertimeLabel,
  getPayTypeLabel,
  calculateFinancialInfoHistory,
  getPayRateTypeLabel,
} from 'utils/assignmentUtils';
import { convertArraysToOneLength } from 'utils/arrayUtils';
import BaseIndicatorNumber from 'components/wrappers/BaseIndicatorNumber';
import { formatNumberAsCurrency } from 'utils/numberUtils';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export interface FinancialInformationHistory {
  vmsMspRate: number;
  vmsMspPortion: number;
  mbpRate: number;
  mbpPortion: number;
  marginPercent: number;
  markupPercent: number;
}

export default function FinancialInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;

  if (!oldData || !newData) return null;

  //Multiple Rates
  const { oldData: oldMultiRates, newData: newMultiRates } =
    convertArraysToOneLength<AssignmentMultiRateEditModel>(
      oldData?.multiRates || [],
      newData?.multiRates || [],
      {
        payType: undefined,
        payRate: undefined,
        billRate: undefined,
        specialInstuctions: undefined,
      },
    );

  const financialInfoOldHistory = calculateFinancialInfoHistory(oldData);
  const financialInfoNewHistory = calculateFinancialInfoHistory(newData);
  const {
    vmsMspRate: vmsMspRateOld,
    vmsMspPortion: vmsMspPortionOld,
    mbpRate: mbpRateOld,
    mbpPortion: mbpPortionOld,
    marginPercent: marginPercentOld,
    markupPercent: markupPercentOld,
  } = financialInfoOldHistory;
  const {
    vmsMspRate: vmsMspRateNew,
    vmsMspPortion: vmsMspPortionNew,
    mbpRate: mbpRateNew,
    mbpPortion: mbpPortionNew,
    marginPercent: marginPercentNew,
    markupPercent: markupPercentNew,
  } = financialInfoNewHistory;

  // Rates calculator
  const hasVmsMspRateChanged = !isEqual(
    vmsMspRateOld || '',
    vmsMspRateNew || '',
  );
  const hasVmsMspPortionChanged = !isEqual(
    vmsMspPortionOld || '',
    vmsMspPortionNew || '',
  );
  const hasMbpRateChanged = !isEqual(mbpRateOld || '', mbpRateNew || '');
  const hasMbpPortionChanged = !isEqual(
    mbpPortionOld || '',
    mbpPortionNew || '',
  );
  const hasRatesCalculatorChanged =
    hasVmsMspRateChanged ||
    hasVmsMspPortionChanged ||
    hasMbpRateChanged ||
    hasMbpPortionChanged;

  // Markup calculator
  const hasMarginPercentChanged = !isEqual(
    marginPercentOld || '',
    marginPercentNew || '',
  );
  const hasMarkupPercentChanged = !isEqual(
    markupPercentOld || '',
    markupPercentNew || '',
  );
  const hasMarkupCalculatorChanged =
    hasMarginPercentChanged || hasMarkupPercentChanged;

  //Financial information
  const hasPayTypeChanged = !isEqual(
    oldData?.financialInfo?.payType || '',
    newData?.financialInfo?.payType || '',
  );
  const hasPayRateChanged = !isEqual(
    oldData?.financialInfo?.payRate || '',
    newData?.financialInfo?.payRate || '',
  );
  const hasBillRateChanged = !isEqual(
    oldData?.financialInfo?.billRate || '',
    newData?.financialInfo?.billRate || '',
  );
  const hasProfitRateChanged = !isEqual(
    oldData?.financialInfo?.profitRate || '',
    newData?.financialInfo?.profitRate || '',
  );
  const hasOvertimeChanged = !isEqual(
    oldData?.financialInfo?.overtime || '',
    newData?.financialInfo?.overtime || '',
  );
  const hasOvertimePayRateChanged = !isEqual(
    oldData?.financialInfo?.overtimePayRate || '',
    newData?.financialInfo?.overtimePayRate || '',
  );
  const hasOvertimeBillRateChanged = !isEqual(
    oldData?.financialInfo?.overtimeBillRate || '',
    newData?.financialInfo?.overtimeBillRate || '',
  );
  const hasDTBillRateChanged = !isEqual(
    oldData?.financialInfo?.dtBillRate || '',
    newData?.financialInfo?.dtBillRate || '',
  );
  const hasDTPayRateChanged = !isEqual(
    oldData?.financialInfo?.dtPayRate || '',
    newData?.financialInfo?.dtPayRate || '',
  );
  const hasMultiRatesChanged = !isEqual(
    oldData?.multiRates?.forEach((multiRate) => unset(multiRate, 'id')) || '',
    newData?.multiRates?.forEach((multiRate) => unset(multiRate, 'id')) || '',
  );

  const hasFinancialInformationChanged =
    hasPayTypeChanged ||
    hasPayRateChanged ||
    hasBillRateChanged ||
    hasProfitRateChanged ||
    hasOvertimeChanged ||
    hasOvertimePayRateChanged ||
    hasOvertimeBillRateChanged ||
    hasDTBillRateChanged ||
    hasDTPayRateChanged ||
    hasMultiRatesChanged;

  const hasPayRateTypeMultiRateChanged = (index: number) =>
    !isEqual(oldMultiRates[index]?.payType, newMultiRates[index]?.payType);

  const hasPayRateMultiRateChanged = (index: number) =>
    !isEqual(
      oldMultiRates[index]?.payRate || '',
      newMultiRates[index]?.payRate || '',
    );

  const hasBillRateMultiRateChanged = (index: number) =>
    !isEqual(
      oldMultiRates[index]?.billRate || '',
      newMultiRates[index]?.billRate || '',
    );

  const hasSpecialInstructionsChanged = (index: number) =>
    !isEqual(
      oldMultiRates[index]?.specialInstuctions || '',
      newMultiRates[index]?.specialInstuctions || '',
    );

  const renderFinancialInformationSection = (
    multiRates: AssignmentMultiRateEditModel[],
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { financialInfo } = assignment;

    const sectionName = 'financial-information';

    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Financial information</div>
        </Col>
        {hasPayTypeChanged && (
          <HistoryField
            title="Pay type"
            value={getPayTypeLabel(financialInfo?.payType)}
            dataId={`${sectionName}-pay-type-field`}
          />
        )}
        {hasPayRateChanged && (
          <HistoryField
            title="Pay rate"
            value={formatNumberAsCurrency({
              num: financialInfo?.payRate,
            })}
            dataId={`${sectionName}-pay-rate-field`}
          />
        )}
        {hasBillRateChanged && (
          <HistoryField
            title="Bill rate"
            value={formatNumberAsCurrency({
              num: financialInfo?.billRate,
            })}
            dataId={`${sectionName}-bill-rate-field`}
          />
        )}
        {hasProfitRateChanged && (
          <HistoryField
            title="Profit rate"
            value={formatNumberAsCurrency({
              num: financialInfo?.profitRate,
            })}
            dataId={`${sectionName}-profit-rate-field`}
          />
        )}
        {hasOvertimeChanged && (
          <HistoryField
            title="Overtime"
            value={getOvertimeLabel(financialInfo?.overtime)}
            dataId={`${sectionName}-overtime-field`}
          />
        )}
        {hasOvertimePayRateChanged && (
          <HistoryField
            title="OT pay rate"
            value={formatNumberAsCurrency({
              num: financialInfo?.overtimePayRate,
            })}
            dataId={`${sectionName}-overtime-pay-rate-field`}
          />
        )}
        {hasOvertimeBillRateChanged && (
          <HistoryField
            title="OT bill rate"
            value={formatNumberAsCurrency({
              num: financialInfo?.overtimeBillRate,
            })}
            dataId={`${sectionName}-overtime-bill-rate-field`}
          />
        )}
        {hasDTBillRateChanged && (
          <HistoryField
            title="DT bill rate"
            value={formatNumberAsCurrency({ num: financialInfo?.dtBillRate })}
            dataId={`${sectionName}-dt-bill-rate-field`}
          />
        )}
        {hasDTPayRateChanged && (
          <HistoryField
            title="DT pay rate"
            value={formatNumberAsCurrency({ num: financialInfo?.dtPayRate })}
            dataId={`${sectionName}-dt-pay-rate-field`}
          />
        )}
        {hasMultiRatesChanged &&
          multiRates.map((multiRate, index) => (
            <React.Fragment key={index}>
              {hasPayRateTypeMultiRateChanged(index) && (
                <HistoryField
                  title={BaseIndicatorNumber({
                    number: index + 2,
                    title: 'Pay rate type',
                  })}
                  value={getPayRateTypeLabel(multiRate?.payType)?.title}
                />
              )}
              {hasPayRateMultiRateChanged(index) && (
                <HistoryField
                  title={BaseIndicatorNumber({
                    number: index + 2,
                    title: 'Pay rate',
                  })}
                  value={formatNumberAsCurrency({
                    num: multiRate?.payRate,
                  })}
                />
              )}
              {hasBillRateMultiRateChanged(index) && (
                <HistoryField
                  title={BaseIndicatorNumber({
                    number: index + 2,
                    title: 'Bill rate',
                  })}
                  value={formatNumberAsCurrency({
                    num: multiRate?.billRate,
                  })}
                />
              )}
              {hasSpecialInstructionsChanged(index) && (
                <HistoryField
                  title={BaseIndicatorNumber({
                    number: index + 2,
                    title: 'Special instructions',
                  })}
                  value={multiRate?.specialInstuctions}
                />
              )}
            </React.Fragment>
          ))}
      </Row>
    );
  };

  const renderRatesCalculatorSection = (
    financialInfo?: FinancialInformationHistory,
  ) => {
    if (!financialInfo) return null;

    const sectionName = 'rates-calculator';
    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Rates calculator</div>
        </Col>
        {hasVmsMspRateChanged && (
          <HistoryField
            title="VMS/MSP Rate"
            value={financialInfo?.vmsMspRate}
            dataId={`${sectionName}-vms-msp-rate-field`}
          />
        )}
        {hasVmsMspPortionChanged && (
          <HistoryField
            title="VMS/MSP Portion"
            value={formatNumberAsCurrency({
              num: financialInfo?.vmsMspPortion,
            })}
            dataId={`${sectionName}-vms-msp-portion-field`}
          />
        )}
        {hasMbpRateChanged && (
          <HistoryField
            title="MBP Rate"
            value={financialInfo?.mbpRate}
            dataId={`${sectionName}-mbp-rate-field`}
          />
        )}
        {hasMbpPortionChanged && (
          <HistoryField
            title="MBP Portion"
            value={formatNumberAsCurrency({ num: financialInfo?.mbpPortion })}
            dataId={`${sectionName}-mbp-portion-field`}
          />
        )}

        <Divider />
      </Row>
    );
  };

  const renderMarkupCalculator = (
    financialInfo?: FinancialInformationHistory,
  ) => {
    if (!financialInfo) return null;

    const sectionName = 'markup-calculator';
    return (
      <Row gutter={[10, 10]}>
        <Col xs={24}>
          <div className={styles.cardTitle}>Markup calculator</div>
        </Col>

        {hasMarginPercentChanged && (
          <HistoryField
            title="Margin %"
            value={formatNumberAsCurrency({
              num: financialInfo?.marginPercent,
              isRightSymbol: false,
              symbol: '%',
            })}
            dataId={`${sectionName}-margin-percent-field`}
          />
        )}
        {hasMarkupPercentChanged && (
          <HistoryField
            title="Markup %"
            value={formatNumberAsCurrency({
              num: financialInfo?.markupPercent,
              isRightSymbol: false,
              symbol: '%',
            })}
            dataId={`${sectionName}-markup-percent-field`}
          />
        )}

        <Divider />
      </Row>
    );
  };

  const renderFinancialInformation = (
    multiRates: AssignmentMultiRateEditModel[],
    assignment?: AssignmentHistoryResponse,
    financialInfo?: FinancialInformationHistory,
  ) => {
    return (
      <>
        {hasFinancialInformationChanged &&
          renderFinancialInformationSection(multiRates, assignment)}
        {hasRatesCalculatorChanged &&
          renderRatesCalculatorSection(financialInfo)}
        {hasMarkupCalculatorChanged && renderMarkupCalculator(financialInfo)}
      </>
    );
  };

  if (
    !hasFinancialInformationChanged &&
    !hasRatesCalculatorChanged &&
    !hasMarkupCalculatorChanged
  )
    return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderFinancialInformation(
        oldMultiRates,
        oldData,
        financialInfoOldHistory,
      )}
      newData={renderFinancialInformation(
        newMultiRates,
        newData,
        financialInfoNewHistory,
      )}
    />
  );
}
